/**
 * Composant Picture optimisé pour World of Numeric
 * Gère automatiquement WebP, AVIF et fallback
 */

interface OptimizedPictureProps {
  src: string; // Nom du fichier sans extension (ex: "AccueilWONNew")
  alt: string;
  className?: string;
  sizes?: string;
  loading?: 'lazy' | 'eager';
  width?: number;
  height?: number;
}

export class OptimizedPicture {
  // Build-time resolved maps for asset URLs (hashed in prod)
  // These ensure images render correctly after build/deploy
  private static avifMap: Record<string, string> = import.meta.glob('/src/assets/optimized/*.avif', {
    eager: true,
    query: '?url',
    import: 'default',
  }) as any;
  private static webpMap: Record<string, string> = import.meta.glob('/src/assets/optimized/*.webp', {
    eager: true,
    query: '?url',
    import: 'default',
  }) as any;
  private static pngMap: Record<string, string> = import.meta.glob('/src/assets/*.{png,jpg,jpeg}', {
    eager: true,
    query: '?url',
    import: 'default',
  }) as any;

  /**
   * Génère le HTML d'une image optimisée avec sources multiples
   */
  public generatePictureHTML(props: OptimizedPictureProps): string {
    const {
      src,
      alt,
      className = '',
      sizes = '(max-width: 768px) 400px, (max-width: 1200px) 800px, 1200px',
      loading = 'lazy',
      width,
      height
    } = props;

    // Détecter l'extension originale
    const originalExt = this.detectOriginalExtension(src);
    
    const srcSets = this.resolveSrcSets(src, originalExt);

    const imgAttributes = [
      `src="${srcSets.fallback}"`,
      `alt="${alt}"`,
      `loading="${loading}"`,
      `decoding="async"`,
      className ? `class="${className}"` : '',
      width ? `width="${width}"` : '',
      height ? `height="${height}"` : ''
    ].filter(Boolean).join(' ');

    return `
      <picture>
        <!-- Format AVIF (le plus efficace) -->
        <source 
          srcset="${srcSets.avif}" 
          type="image/avif"
          ${sizes ? `sizes="${sizes}"` : ''}
        >
        
        <!-- Format WebP (bon support) -->
        <source 
          srcset="${srcSets.webp}" 
          type="image/webp"
          ${sizes ? `sizes="${sizes}"` : ''}
        >
        
        <!-- Fallback (image originale) -->
        <img ${imgAttributes}>
      </picture>
    `.trim();
  }

  /**
   * Génère le srcset pour WebP avec différentes tailles
   */
  private generateWebPSrcSet(src: string): string {
    const sizes = [400, 800, 1200];
    const parts: string[] = [];
    sizes.forEach(size => {
      // Try patterns like `${src}-${size}w.webp`
      const key = `/src/assets/optimized/${src}-${size}w.webp`;
      const url = (OptimizedPicture.webpMap[key] as string) || '';
      if (url) parts.push(`${url} ${size}w`);
    });
    // Fallback to a single webp if sized variants are not found
    if (parts.length === 0) {
      const singleKey = `/src/assets/optimized/${src}.webp`;
      const single = (OptimizedPicture.webpMap[singleKey] as string) || '';
      if (single) parts.push(`${single} 800w`);
    }
    return parts.join(', ');
  }

  /**
   * Détecte l'extension du fichier original
   */
  private detectOriginalExtension(src: string): string {
    const candidates = ['png', 'jpg', 'jpeg'];
    for (const ext of candidates) {
      const key = `/src/assets/${src}.${ext}`;
      if (OptimizedPicture.pngMap[key]) return ext;
    }
    return 'png';
  }

  private resolveSrcSets(src: string, originalExt: string): { avif?: string; webp: string; fallback: string } {
    const avifKey = `/src/assets/optimized/${src}.avif`;
    const avifUrl = (OptimizedPicture.avifMap[avifKey] as string) || undefined;

    const webp = this.generateWebPSrcSet(src);

    const fallbackKey = `/src/assets/${src}.${originalExt}`;
    const fallbackUrl = (OptimizedPicture.pngMap[fallbackKey] as string) || fallbackKey;

    return {
      avif: avifUrl,
      webp,
      fallback: fallbackUrl,
    };
  }

  /**
   * Méthode statique pour usage facile
   */
  public static create(props: OptimizedPictureProps): string {
    const instance = new OptimizedPicture();
    return instance.generatePictureHTML(props);
  }
}

export default OptimizedPicture;
